import type { ReactNode } from 'react'
import { Fragment } from 'react'
import { Box } from '@mantine/core'
import type { StoryblokRichtext } from '@percent/cause-dashboard/storyblok/types/storyblok.d.ts'
import type { LinkButton, NativeApplyModalButton } from '@percent/cause-dashboard/storyblok/types/storyblok-components'
import { Typography, type TypographyVariant } from '@percent/lemonade'
import { RichTextLinkButton } from './richTextLinkButton/RichTextLinkButton'
import { RichTextNativeApplyModalButton } from './richTextNativeApplyModalButton/RichTextNativeApplyModalButton'
import styles from './RichTextRenderer.module.scss'
import { ProductOfferCTACardsGroup } from '@percent/cause-dashboard/common/components'

function isLinkButtonBlok(raw: unknown): raw is LinkButton {
  return Boolean(raw && typeof raw === 'object' && (raw as { component?: string }).component === 'link_button')
}

function isNativeApplyModalButtonBlok(raw: unknown): raw is NativeApplyModalButton {
  return Boolean(
    raw && typeof raw === 'object' && (raw as { component?: string }).component === 'native_apply_modal_button'
  )
}

const headingVariant = (level: number): TypographyVariant => {
  const clamped = Math.min(Math.max(Math.floor(level), 1), 6)
  return `h${clamped}` as TypographyVariant
}

function applyMarks(text: string, marks: StoryblokRichtext[] | undefined, keyPrefix: string): ReactNode {
  if (!marks?.length) {
    return text
  }

  return marks.reduce<ReactNode>((acc, mark, index) => {
    const key = `${keyPrefix}-m${index}`
    switch (mark.type) {
      case 'bold':
        return (
          <strong key={key} className={styles.bold}>
            {acc}
          </strong>
        )
      case 'italic':
        return (
          <em key={key} className={styles.italic}>
            {acc}
          </em>
        )
      case 'underline':
        return (
          <span key={key} className={styles.underline}>
            {acc}
          </span>
        )
      case 'strike':
        return (
          <span key={key} className={styles.strike}>
            {acc}
          </span>
        )
      case 'code':
        return (
          <code key={key} className={styles.inlineCode}>
            {acc}
          </code>
        )
      case 'link': {
        const href = (mark.attrs?.href as string | undefined) ?? '#'
        const target = (mark.attrs?.target as string | undefined) || undefined
        return (
          <a
            key={key}
            href={href}
            target={target === '_blank' ? '_blank' : undefined}
            rel={target === '_blank' ? 'noreferrer' : undefined}
            className={styles.link}
          >
            {acc}
          </a>
        )
      }
      default:
        return acc
    }
  }, text)
}

function renderInline(node: StoryblokRichtext, keyPrefix: string): ReactNode {
  if (node.type === 'text') {
    return applyMarks(node.text ?? '', node.marks, keyPrefix)
  }
  if (node.type === 'hard_break') {
    return <br key={keyPrefix} />
  }
  return null
}

function renderBlockContent(parent: StoryblokRichtext, keyPrefix: string): ReactNode {
  const parts = parent.content ?? []
  return parts.map((child, index) => {
    const key = `${keyPrefix}-${index}`
    if (child.type === 'text' || child.type === 'hard_break') {
      return <Fragment key={key}>{renderInline(child, key)}</Fragment>
    }
    return <Fragment key={key}>{renderNode(child, key)}</Fragment>
  })
}

function renderList(kind: 'bullet' | 'ordered', node: StoryblokRichtext, keyPrefix: string): ReactNode {
  const ListTag = kind === 'bullet' ? 'ul' : 'ol'
  const items = (node.content ?? []).map((item, index) => {
    const key = `${keyPrefix}-li-${index}`
    if (item.type !== 'list_item') {
      return (
        <li key={key} className={styles.listItem}>
          {renderNode(item, `${key}-nested`)}
        </li>
      )
    }
    return (
      <li key={key} className={styles.listItem}>
        {renderBlockContent(item, `${key}-content`)}
      </li>
    )
  })

  return (
    <ListTag className={kind === 'bullet' ? styles.bulletList : styles.orderedList} key={keyPrefix}>
      {items}
    </ListTag>
  )
}

function renderNode(node: StoryblokRichtext, keyPrefix: string): ReactNode {
  switch (node.type) {
    case 'doc':
      return (
        <div className={styles.docRoot}>{(node.content ?? []).map((c, i) => renderNode(c, `${keyPrefix}-d${i}`))}</div>
      )
    case 'paragraph':
      return (
        <Typography key={keyPrefix} variant="large" className={styles.paragraph}>
          {renderBlockContent(node, `${keyPrefix}-p`)}
        </Typography>
      )
    case 'heading': {
      const level = Number(node.attrs?.level ?? 2)
      return (
        <Typography key={keyPrefix} variant={headingVariant(level)} className={styles.heading}>
          {renderBlockContent(node, `${keyPrefix}-h`)}
        </Typography>
      )
    }
    case 'bullet_list':
      return renderList('bullet', node, keyPrefix)
    case 'ordered_list':
      return renderList('ordered', node, keyPrefix)
    case 'blockquote':
      return (
        <Box key={keyPrefix} component="blockquote" className={styles.blockquote}>
          {(node.content ?? []).map((c, i) => renderNode(c, `${keyPrefix}-q${i}`))}
        </Box>
      )
    case 'code_block':
      return (
        <pre key={keyPrefix} className={styles.codeBlock}>
          <code>{node.content?.map((c, i) => renderInline(c as StoryblokRichtext, `${keyPrefix}-c${i}`))}</code>
        </pre>
      )
    case 'horizontal_rule':
      return <hr key={keyPrefix} className={styles.horizontalRule} />
    case 'blok': {
      const attrs = node.attrs as { body?: unknown[] } | undefined
      const body = Array.isArray(attrs?.body) ? attrs.body : []
      if (!body.length) {
        return null
      }

      return (
        <Fragment key={keyPrefix}>
          {body.map((raw, i) => {
            const k = `${keyPrefix}-emb${i}`
            if (isLinkButtonBlok(raw)) {
              return <RichTextLinkButton key={k} blok={raw} />
            }
            if (isNativeApplyModalButtonBlok(raw)) {
              return <RichTextNativeApplyModalButton key={k} blok={raw} />
            }
            if (
              raw &&
              typeof raw === 'object' &&
              (raw as { component?: string }).component === 'product_offer_cta_cards_group'
            ) {
              return <ProductOfferCTACardsGroup key={k} body={[raw]} keyPrefix={k} />
            }
            return null
          })}
        </Fragment>
      )
    }
    default:
      return null
  }
}

/** Walks Storyblok / ProseMirror-style rich text JSON and renders Lemonade Typography (and semantic lists/links). */
export function storyblokRichtextDocToReact(doc: StoryblokRichtext | undefined | null): ReactNode {
  if (!doc || doc.type !== 'doc') {
    return null
  }

  return renderNode(doc, 'sb-root')
}
